// Native WebSocket implementation for Django Channels
import { store } from '../store';
import { addNotification } from '../store/slices/uiSlice';
import { logError } from '../utils/errorLogger';
import apiClient from './api';

type EventListener<T = any> = (data: T) => void;
type EventType = 'event_updated' | 'event_created' | 'event_deleted' | 'guest_response' | 'connection_status' | 'event_statistics_update' | 'notification' | 'notification_update' | 'websocket_error' | 'subscription_confirmed' | 'whatsapp_status_update';

class WebSocketService {
  private socket: WebSocket | null = null;
  private listeners: Map<EventType, Set<EventListener>> = new Map();
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private baseUrl: string;
  private hasShownConnectionNotification: boolean = false;
  private _notificationSocket: WebSocket | null = null;
  private notificationReconnectAttempts: number = 0;
  private maxNotificationReconnectAttempts: number = 3;
  private notificationReconnectTimeout: NodeJS.Timeout | null = null;
  private isConnectingNotifications: boolean = false;
  private pendingSubscriptions: Set<string> = new Set();
  
  constructor() {
    this.baseUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
    this.setupConnection();
  }
  
  // Event listener management
  on<T = any>(eventType: EventType, listener: EventListener<T>): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    
    this.listeners.get(eventType)!.add(listener);
    
    // Ensure WebSocket is connected when adding listeners
    if (!this.isConnected) {
      this.connect().catch(error => {
        // Error is already logged in connect
      });
    }
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(eventType);
        }
      }
    };
  }
  
  private emit<T = any>(eventType: EventType, data: T): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          logError(`Error in WebSocket event listener for ${eventType}`, error);
        }
      });
    }
  }
  
  // WebSocket connection management
  private setupConnection(): void {
    try {
      this.connect().catch(error => {
        // Error is already logged in connect
      });
      this.setupPingPong();
      
    } catch (error) {
      logError('Failed to initialize WebSocket connection', error);
      this.handleConnectionError();
    }
  }
  
  private setupEventHandlers(): void {
    if (!this.socket) return;
    
    // Connection events
    this.socket.onopen = () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('connection_status', { connected: true });
      
      // Clear any pending reconnect timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      
      // Process pending subscriptions with a small delay to ensure WebSocket is fully ready
      if (this.pendingSubscriptions.size > 0) {
        setTimeout(() => {
          const subscriptionsToProcess = Array.from(this.pendingSubscriptions);
          this.pendingSubscriptions.clear();
          
          subscriptionsToProcess.forEach(eventId => {
            const success = this.safeSend({ type: 'subscribe_event', event_id: eventId }, eventId);
            if (!success) {
              // Re-queue if send failed
              this.pendingSubscriptions.add(eventId);
            }
          });
        }, 100); // Small delay to ensure WebSocket is fully ready
      }
      
      // Connection restored - no notification shown
      if (!this.hasShownConnectionNotification) {
        this.hasShownConnectionNotification = true;
      }
    };
    
    this.socket.onclose = (event) => {
      this.isConnected = false;
      this.hasShownConnectionNotification = false; // Reset flag on disconnect
      this.emit('connection_status', { connected: false });
      
      if (event.code !== 1000) { // Not a normal closure
        this.handleReconnection();
      }
    };
    
    this.socket.onerror = (error) => {
      logError('WebSocket connection error', error);
      this.handleConnectionError();
    };
    
    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleWebSocketMessage(data);
      } catch (error) {
        logError('Error parsing WebSocket message', error);
      }
    };
  }
  
  private handleWebSocketMessage(data: any): void {
    switch (data.type) {
      case 'guest_response_update':
        this.emit('guest_response', data.data);
        break;
        
      case 'guest_response':
        this.emit('guest_response', data);
        break;
        
      case 'event_statistics_update':
        this.emit('event_statistics_update', data.data);
        break;
        
      case 'notification':
        this.emit('notification', data.data);
        
        store.dispatch(addNotification({
          type: data.data.type || 'info',
          title: data.data.title || 'Notification',
          message: data.data.message || 'You have a new notification',
        }));
        break;
        
      case 'connection_established':
        break;
        
      case 'subscription_confirmed':
        // Emit subscription_confirmed event for components to handle
        this.emit('subscription_confirmed', {
          event_id: data.event_id,
          event_title: data.event_title,
          timestamp: new Date().toISOString()
        });
        break;
        
      case 'pong':
        // Handle ping/pong for connection health
        break;

      case 'whatsapp_status_update':
        this.emit('whatsapp_status_update', data);
        break;
        
      case 'error':
        logError('WebSocket error received', new Error(data.message || 'WebSocket error occurred'));
        // Emit error event for components to handle
        this.emit('websocket_error', {
          type: 'error',
          message: data.message || 'WebSocket error occurred',
          timestamp: new Date().toISOString(),
          originalData: data
        });
        break;
        
      default:
        break;
    }
  }
  
  private setupPingPong(): void {
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.socket && this.isConnected && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
      
      // Also retry any pending subscriptions
      this.retryPendingSubscriptions();
    }, 30000);
  }
  
  private retryPendingSubscriptions(): void {
    if (this.pendingSubscriptions.size > 0 && this.socket && this.socket.readyState === WebSocket.OPEN) {
      const subscriptionsToRetry = Array.from(this.pendingSubscriptions);
      this.pendingSubscriptions.clear();
      
      subscriptionsToRetry.forEach(eventId => {
        this.safeSend({ type: 'subscribe_event', event_id: eventId }, eventId);
      });
    }
  }
  
  private safeSend(message: any, context?: string): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return false;
    }
    
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      logError('Failed to send WebSocket message', error);
      return false;
    }
  }
  
  private handleConnectionError(): void {
    this.isConnected = false;
    this.emit('connection_status', { connected: false });
    
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.handleReconnection();
    }
    // Connection will silently attempt to reconnect in the background
  }
  
  private handleReconnection(): void {
    if (this.reconnectTimeout) return; // Already trying to reconnect
    
    this.reconnectAttempts++;
    
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 16000);
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, delay);
  }
  
  // Public methods
  async connect(): Promise<void> {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return; // Already connected
    }
    
    try {
      // Try to get token from Redux state first
      const state = store.getState();
      let token = state.auth.tokens?.access_token;
      
      // If no token in Redux, fetch from API (reads from httpOnly cookie)
      if (!token || token === 'undefined' || token === undefined || token === null) {
        try {
          const response = await apiClient.get('/auth/websocket-token/');
          
          if (response.data?.success && response.data?.data?.access_token) {
            token = response.data.data.access_token;
          }
        } catch (error: any) {
          // Handle 403/401 gracefully - user is not authenticated
          const status = error.response?.status;
          if (status === 403 || status === 401) {
            // User is not authenticated - this is expected, don't log as error
            // Don't connect if no token - user needs to log in first
            return;
          }
          
          // For other errors (network, 500, etc.), log them
          logError('Failed to get WebSocket token from API', error);
          // Don't connect if no token - user needs to log in first
          return;
        }
      }
      
      if (!token || token === 'undefined' || token === undefined || token === null) {
        // No token available - user needs to log in first
        // Don't log as error, this is expected when not authenticated
        return;
      }
      
      const wsUrl = `${this.baseUrl}/ws/guest-responses/?token=${token}`;
      
      this.socket = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      logError('Failed to create WebSocket connection', error);
      this.handleConnectionError();
    }
  }
  
  disconnect(): void {
    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
  }
  
  subscribeToEvent(eventId: string): void {
    // Validate eventId format
    if (!eventId || typeof eventId !== 'string' || eventId.trim() === '') {
      logError('Invalid eventId provided for WebSocket subscription', new Error('Invalid event ID'));
      this.emit('websocket_error', {
        type: 'error',
        message: 'Invalid event ID provided',
        timestamp: new Date().toISOString()
      });
      return;
    }
    
    const success = this.safeSend({ type: 'subscribe_event', event_id: eventId }, eventId);
    if (!success) {
      // If send fails, queue the subscription
      this.pendingSubscriptions.add(eventId);
    }
  }
  
  // Legacy polling methods (for backward compatibility)
  startEventPolling(eventId?: string): string {
    // Subscribe to WebSocket instead of polling
    if (eventId) {
      this.subscribeToEvent(eventId);
    }
    return eventId ? `event_${eventId}` : 'events_global';
  }
  
  stopPolling(pollId: string): void {
    // No-op for WebSocket implementation
  }
  
  stopAllPolling(): void {
    // No-op for WebSocket implementation
  }
  
  // Manual refresh trigger
  async refreshData(dataType: 'events' | 'categories' | 'series' = 'events'): Promise<void> {
    this.emit('event_updated', { 
      type: 'manual_refresh', 
      dataType,
      timestamp: new Date().toISOString() 
    });
  }
  
  // Status getters
  get connectionStatus(): boolean {
    return this.isConnected;
  }
  
  get activePollingCount(): number {
    return this.isConnected ? 1 : 0; // WebSocket is always "polling" when connected
  }

  get notificationSocket(): WebSocket | null {
    return this._notificationSocket;
  }

  get isNotificationConnected(): boolean {
    return this._notificationSocket !== null && this._notificationSocket.readyState === WebSocket.OPEN;
  }
  
  // Notification WebSocket connection
  async connectNotifications(): Promise<void> {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnectingNotifications) {
      return;
    }

    if (this._notificationSocket && this._notificationSocket.readyState === WebSocket.OPEN) {
      return;
    }

    // Check if we've exceeded max reconnection attempts
    if (this.notificationReconnectAttempts >= this.maxNotificationReconnectAttempts) {
      return;
    }

    this.isConnectingNotifications = true;
    
    // Try to get token from Redux state first
    let token: string | null = null;
    try {
      const state = store.getState();
      token = state.auth.tokens?.access_token || null;
    } catch (e) {
      // Silent fail
    }
    
    // If no token in Redux, fetch from API (reads from httpOnly cookie)
    if (!token) {
      try {
        const response = await apiClient.get('/auth/websocket-token/');
        
        if (response.data?.success && response.data?.data?.access_token) {
          token = response.data.data.access_token;
        }
      } catch (error) {
        logError('Failed to get WebSocket token from API', error);
        this.isConnectingNotifications = false;
        return;
      }
    }
    
    if (!token || token === 'undefined' || token === null) {
      logError('No authentication token found for notification WebSocket connection');
      this.isConnectingNotifications = false;
      return;
    }
    
    const notificationUrl = `${this.baseUrl}/ws/notifications/?token=${token}`;

    this._notificationSocket = new WebSocket(notificationUrl);

    this._notificationSocket.onopen = () => {
      this.notificationReconnectAttempts = 0; // Reset reconnection attempts on successful connection
      this.isConnectingNotifications = false;
    };

    this._notificationSocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleNotificationMessage(data);
      } catch (error) {
        logError('Error parsing notification WebSocket message', error);
      }
    };

    this._notificationSocket.onclose = (event) => {
      this._notificationSocket = null;
      this.isConnectingNotifications = false;
      
      // Only attempt to reconnect if we have listeners and haven't exceeded max attempts
      if ((this.listeners.has('notification') || this.listeners.has('notification_update')) && 
          this.notificationReconnectAttempts < this.maxNotificationReconnectAttempts) {
        
        this.notificationReconnectAttempts++;
        
        this.notificationReconnectTimeout = setTimeout(() => {
          this.connectNotifications().catch(error => {
            // Error is already logged in connectNotifications
          });
        }, 5000);
      }
    };

    this._notificationSocket.onerror = (error) => {
      logError('Notification WebSocket error', error);
      this.isConnectingNotifications = false;
    };
  }

  private handleNotificationMessage(data: any): void {
    switch (data.type) {
      case 'notification':
        this.emit('notification', data.data);
        break;
      case 'notification_update':
        this.emit('notification_update', data.data);
        break;
      case 'connection_established':
        break;
      case 'pong':
        // Handle pong response
        break;
      default:
        break;
    }
  }

  disconnectNotifications(): void {
    if (this._notificationSocket) {
      this._notificationSocket.close(1000, 'Disconnecting notifications');
      this._notificationSocket = null;
    }
    
    if (this.notificationReconnectTimeout) {
      clearTimeout(this.notificationReconnectTimeout);
      this.notificationReconnectTimeout = null;
    }
    
    this.notificationReconnectAttempts = 0;
  }

  resetNotificationReconnection(): void {
    this.notificationReconnectAttempts = 0;
    this.isConnectingNotifications = false;
  }

  disableNotificationWebSocket(): void {
    this.disconnectNotifications();
    this.notificationReconnectAttempts = this.maxNotificationReconnectAttempts; // Prevent future connections
  }

  enableNotificationWebSocket(): void {
    this.notificationReconnectAttempts = 0;
    this.isConnectingNotifications = false;
  }

  // Usher WebSocket connection
  async connectUsherWebSocket(eventId?: string): Promise<WebSocket | null> {
    // Try to get token from Redux state first
    let token: string | null = null;
    try {
      const state = store.getState();
      token = state.auth.tokens?.access_token || null;
    } catch (e) {
      // Silent fail
    }
    
    // If no token in Redux, fetch from API (reads from httpOnly cookie)
    if (!token) {
      try {
        const response = await apiClient.get('/auth/websocket-token/');
        
        if (response.data?.success && response.data?.data?.access_token) {
          token = response.data.data.access_token;
        }
      } catch (error) {
        logError('Failed to get WebSocket token from API for usher connection', error);
        return null;
      }
    }
    
    if (!token || token === 'undefined' || token === null) {
      logError('No authentication token found for usher WebSocket connection');
      return null;
    }
    
    const usherUrl = eventId 
      ? `${this.baseUrl}/ws/ushers/${eventId}/?token=${token}`
      : `${this.baseUrl}/ws/ushers/?token=${token}`;
    
    const usherSocket = new WebSocket(usherUrl);
    
    usherSocket.onopen = () => {
      // Connection established
    };
    
    usherSocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleWebSocketMessage(data);
      } catch (error) {
        logError('Error parsing usher WebSocket message', error);
      }
    };
    
    usherSocket.onclose = (event) => {
      // Connection closed
    };
    
    usherSocket.onerror = (error) => {
      logError('Usher WebSocket error', error);
    };
    
    return usherSocket;
  }

  // Cleanup
  destroy(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.socket) {
      this.socket.close(1000, 'Service destroy');
      this.socket = null;
    }

    this.disconnectNotifications();
    
    this.listeners.clear();
    this.isConnected = false;
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
export default webSocketService;
